// src/components/resources/calculators/LTVCalculator.logic.ts

/**
 * Pure, framework-free LTV math — extracted from `LTVCalculator.tsx` so it can be
 * unit-tested directly (A7/D4 / BP1: the "untested inline-math" class that let
 * the calculator stat-bugs ship). The component imports `calculateMetrics` and
 * renders; all arithmetic lives here.
 */

export interface CalculatorInputs {
  averagePurchaseValue: number
  purchaseFrequency: number // purchases per year
  /** Fallback planning horizon in years when annual churn is 0%. */
  customerLifespan: number
  churnRate: number // annual percentage
  acquisitionCost: number
  profitMargin: number // percentage
}

export const MAX_MODELED_CUSTOMER_LIFESPAN_YEARS = 100
export const MIN_FALLBACK_CUSTOMER_LIFESPAN_YEARS = 1

function finiteNumberOrZero(value: number): number {
  return Number.isFinite(value) ? value : 0
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export interface CalculatorResults {
  monthlyRevenue: number
  annualRevenue: number
  lifetimeRevenue: number
  lifetimeProfit: number
  netLTV: number
  roi: number
  monthsToROI: number
  retentionRate: number
  effectiveCustomerLifespan: number
}

/**
 * Compute the full LTV result set from the calculator inputs.
 *
 * Edge cases that are part of the contract:
 * - **Annual churn above 0%** drives customer lifespan as `1 / churn`, capped
 *   at 100 years (for example, 20% annual churn implies a 5-year average
 *   lifespan). This keeps the churn input load-bearing without introducing a
 *   cliff at tiny-but-positive churn rates.
 * - **Zero or non-finite annual churn** falls back to the explicit
 *   `customerLifespan` planning horizon because the churn-derived lifespan would
 *   be unbounded. The fallback horizon is clamped to 1–100 years.
 * - **Churn above 100%** is clamped to 100% for retention and lifespan math.
 * - **Negative or non-finite revenue-side inputs** are treated as 0; profit
 *   margin is clamped to 0–100%.
 * - **Zero acquisition cost** → `roi` is `Number.POSITIVE_INFINITY` (infinite
 *   return on zero investment), which the UI renders as `N/A` rather than a
 *   misleading `0%`.
 * - **Non-positive monthly profit** → `monthsToROI` is `0` (no payback to model).
 */
export function calculateMetrics(inputs: CalculatorInputs): CalculatorResults {
  const averagePurchaseValue = Math.max(
    finiteNumberOrZero(inputs.averagePurchaseValue),
    0,
  )
  const purchaseFrequency = Math.max(
    finiteNumberOrZero(inputs.purchaseFrequency),
    0,
  )
  const acquisitionCost = Math.max(
    finiteNumberOrZero(inputs.acquisitionCost),
    0,
  )
  const profitMargin = clamp(finiteNumberOrZero(inputs.profitMargin), 0, 100)
  const churnRate = clamp(finiteNumberOrZero(inputs.churnRate), 0, 100)
  const fallbackCustomerLifespan = clamp(
    finiteNumberOrZero(inputs.customerLifespan),
    MIN_FALLBACK_CUSTOMER_LIFESPAN_YEARS,
    MAX_MODELED_CUSTOMER_LIFESPAN_YEARS,
  )
  const retentionRate = 100 - churnRate
  const churnDerivedLifespan =
    churnRate > 0 ? 100 / churnRate : fallbackCustomerLifespan
  const effectiveCustomerLifespan = Math.min(
    churnDerivedLifespan,
    MAX_MODELED_CUSTOMER_LIFESPAN_YEARS,
  )
  const monthlyRevenue = (averagePurchaseValue * purchaseFrequency) / 12
  const annualRevenue = monthlyRevenue * 12
  const lifetimeRevenue = annualRevenue * effectiveCustomerLifespan

  const profitMarginMultiplier = profitMargin / 100
  const lifetimeProfit = lifetimeRevenue * profitMarginMultiplier
  const netLTV = lifetimeProfit - acquisitionCost

  // Zero CAC → infinite return on zero investment, not 0% (displayed as N/A).
  const roi =
    acquisitionCost > 0
      ? (netLTV / acquisitionCost) * 100
      : Number.POSITIVE_INFINITY
  const monthlyProfit = (annualRevenue * profitMarginMultiplier) / 12
  const monthsToROI = monthlyProfit > 0 ? acquisitionCost / monthlyProfit : 0

  return {
    monthlyRevenue,
    annualRevenue,
    lifetimeRevenue,
    lifetimeProfit,
    netLTV,
    roi,
    monthsToROI,
    retentionRate,
    effectiveCustomerLifespan,
  }
}
